export type DraggableAxis = 'both' | 'y';

/**
 * Rend un panneau déplaçable via sa poignée (handle). `axis: 'y'` ignore le
 * déplacement horizontal (barre pleine largeur qu'on ne fait que monter/descendre).
 */
export function makeDraggable(
  panel: HTMLElement,
  handle: HTMLElement,
  options: { axis?: DraggableAxis } = {}
): void {
  const axis = options.axis ?? 'both';
  const posKey = `multirx-drag-${panel.dataset.dragId ?? panel.id ?? 'panel'}`;
  const saved = sessionStorage.getItem(posKey);
  if (saved) {
    try {
      const { left, top } = JSON.parse(saved) as { left: number; top: number };
      if (axis === 'both') {
        panel.style.left = `${left}px`;
        panel.style.right = 'auto';
      }
      panel.style.top = `${top}px`;
      panel.style.bottom = 'auto';
    } catch {
      /* ignore */
    }
  }

  let dragging = false;
  let startX = 0;
  let startY = 0;
  let startLeft = 0;
  let startTop = 0;

  const onPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0) return;
    dragging = true;
    handle.setPointerCapture(e.pointerId);
    const rect = panel.getBoundingClientRect();
    const parent = panel.offsetParent;
    const parentRect = parent?.getBoundingClientRect() ?? { left: 0, top: 0 };
    startX = e.clientX;
    startY = e.clientY;
    startLeft = rect.left - parentRect.left;
    startTop = rect.top - parentRect.top;
    if (axis === 'both') {
      panel.style.left = `${startLeft}px`;
      panel.style.right = 'auto';
    }
    panel.style.top = `${startTop}px`;
    panel.style.bottom = 'auto';
    e.preventDefault();
  };

  const onPointerMove = (e: PointerEvent): void => {
    if (!dragging) return;
    const parent = panel.offsetParent;
    const pw = parent?.clientWidth ?? window.innerWidth;
    const ph = parent?.clientHeight ?? window.innerHeight;
    const w = panel.offsetWidth;
    const h = panel.offsetHeight;
    let top = startTop + (e.clientY - startY);
    top = Math.max(0, Math.min(top, ph - h));
    panel.style.top = `${top}px`;
    if (axis === 'both') {
      let left = startLeft + (e.clientX - startX);
      left = Math.max(0, Math.min(left, pw - w));
      panel.style.left = `${left}px`;
    }
  };

  const onPointerUp = (e: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    handle.releasePointerCapture(e.pointerId);
    sessionStorage.setItem(
      posKey,
      JSON.stringify({
        left: parseFloat(panel.style.left) || 0,
        top: parseFloat(panel.style.top) || 0,
      })
    );
  };

  handle.addEventListener('pointerdown', onPointerDown);
  handle.addEventListener('pointermove', onPointerMove);
  handle.addEventListener('pointerup', onPointerUp);
  handle.addEventListener('pointercancel', onPointerUp);
}
